import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildCutSequence,
  buildCutSimulationCaptionDescriptor,
  buildCutSimulationSteps,
  getActiveStockForSimulationStep,
  getSheetSize,
  shouldShowCutSequence,
} from "@plaatplan/core";
import { resolveWorkshopMessage } from "@plaatplan/i18n";
import { useLocale } from "../i18n/useLocale";
import { ExportBoard } from "./ExportBoard";
import { DismissibleMobileAppPromo } from "../promo/DismissibleMobileAppPromo";

function CutSimulatorIcon() {
  return (
    <svg
      className="cut-simulator-open-icon"
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <rect x="2.5" y="3" width="15" height="14" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2.5 10h15" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="7" cy="10" r="2.1" fill="currentColor" />
      <path d="M12.5 12.2 15.5 13.5 12.5 14.8" fill="currentColor" />
    </svg>
  );
}

function isEditableKeyboardTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") return true;
  return target.isContentEditable;
}

function useIsMobileExportView() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches
  ));

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsMobile(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

export function CutSimulator({
  sourcePanel,
  sheet,
  strategy,
  exportDraft,
  boardProps,
  isOpen: simulatorOpen = false,
  onOpen,
  onClose,
  showMobileAppPromo = false,
  enableMobileHandoff = false,
  onWorkshopHandoff,
}) {
  const { display } = useLocale();
  const { length, width } = getSheetSize(sheet, sourcePanel);
  const margin = Number(sourcePanel.margin || 0);
  const showCuts = shouldShowCutSequence(strategy, exportDraft.showCutSequence);
  const isMobileView = useIsMobileExportView();
  const panelId = `cut-simulator-panel-${sheet.index}`;
  const openButtonRef = useRef(null);
  const panelHeadingRef = useRef(null);

  const cutSequence = useMemo(
    () => (showCuts
      ? buildCutSequence(sheet, sourcePanel, {
        strategy,
        stripsNoCrosscut: exportDraft.stripsNoCrosscut,
        minOffcutLength: Number(exportDraft.minOffcutLength || 500),
        minOffcutWidth: Number(exportDraft.minOffcutWidth || 300),
      })
      : []),
    [showCuts, sheet, sourcePanel, strategy, exportDraft],
  );

  const steps = useMemo(
    () => buildCutSimulationSteps(cutSequence, length, width, margin),
    [cutSequence, length, width, margin],
  );

  const maxStep = cutSequence.length;
  const [currentStep, setCurrentStep] = useState(0);
  const canSimulate = showCuts && cutSequence.length > 0;
  const atStart = currentStep === 0;
  const atEnd = currentStep >= maxStep;

  const stepStatus = useMemo(() => {
    if (currentStep === 0) {
      return {
        title: display.t("web.werkplan.simulator.noCutsTitle"),
        detail: display.t("web.werkplan.simulator.noCutsDetail"),
      };
    }

    if (atEnd) {
      return {
        title: display.t("web.werkplan.simulator.endLayoutTitle"),
        detail: display.t("web.werkplan.simulator.endLayoutDetail"),
      };
    }

    const cut = cutSequence[currentStep - 1];
    const caption = resolveWorkshopMessage(
      display,
      buildCutSimulationCaptionDescriptor(cut, length, width),
    );

    return {
      title: display.t("web.werkplan.simulator.cutStepTitle", {
        number: cut.number,
        caption,
      }),
      detail: null,
    };
  }, [currentStep, atEnd, cutSequence, length, width, display]);

  const stepProgressLabel = useMemo(() => {
    const label = atStart
      ? display.t("web.werkplan.simulator.stepStart")
      : display.t("web.werkplan.simulator.stepCut", { current: currentStep });

    return display.t("web.werkplan.simulator.stepProgress", {
      label,
      max: maxStep,
    });
  }, [atStart, currentStep, maxStep, display]);

  const mobileCutStepTitles = useMemo(
    () => cutSequence.map((cut) => {
      const caption = resolveWorkshopMessage(
        display,
        buildCutSimulationCaptionDescriptor(cut, length, width),
      );

      return display.t("web.werkplan.simulator.cutStepTitle", {
        number: cut.number,
        caption,
      });
    }),
    [cutSequence, length, width, display],
  );

  const activeStock = useMemo(() => {
    if (!simulatorOpen) return null;
    return getActiveStockForSimulationStep(currentStep, steps, cutSequence);
  }, [simulatorOpen, currentStep, steps, cutSequence]);

  useEffect(() => {
    setCurrentStep(0);
  }, [sheet.index, strategy, cutSequence.length]);

  useEffect(() => {
    if (!simulatorOpen) {
      setCurrentStep(0);
    }
  }, [simulatorOpen]);

  useEffect(() => {
    if (isMobileView && simulatorOpen) {
      onClose?.();
    }
  }, [isMobileView, simulatorOpen, onClose]);

  useEffect(() => {
    if (!simulatorOpen) return undefined;

    const onKeyDown = (event) => {
      if (isEditableKeyboardTarget(event.target)) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setCurrentStep((value) => Math.max(0, value - 1));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        setCurrentStep((value) => Math.min(maxStep, value + 1));
      } else if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        setCurrentStep(0);
        window.requestAnimationFrame(() => {
          openButtonRef.current?.focus();
        });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [simulatorOpen, maxStep, currentStep, onClose]);

  const openSimulator = () => {
    onOpen?.();
    window.requestAnimationFrame(() => {
      panelHeadingRef.current?.focus();
    });
  };

  const closeSimulator = () => {
    onClose?.();
    setCurrentStep(0);
    window.requestAnimationFrame(() => {
      openButtonRef.current?.focus();
    });
  };

  if (isMobileView) {
    return (
      <div className="cut-simulator-wrap cut-simulator-wrap--mobile-static">
        <ExportBoard
          {...boardProps}
          sourcePanel={sourcePanel}
          sheet={sheet}
          strategy={strategy}
          showCutSequence={showCuts}
        />
        {canSimulate ? (
          <section
            aria-labelledby={`${panelId}-mobile-steps`}
            className="cut-simulator-mobile-steps"
          >
            <h4 id={`${panelId}-mobile-steps`}>
              {display.t("web.werkplan.simulator.mobileStepsHeading")}
            </h4>
            <p className="cut-simulator-mobile-steps-note">
              {display.t("web.werkplan.simulator.mobileStepsNote")}
            </p>
            <ol>
              {mobileCutStepTitles.map((title, index) => (
                <li key={`${sheet.index}-mobile-cut-${index}`}>{title}</li>
              ))}
            </ol>
          </section>
        ) : null}
        {showMobileAppPromo ? (
          <DismissibleMobileAppPromo
            enableHandoff={enableMobileHandoff}
            layout="phone"
            onHandoff={onWorkshopHandoff}
            variant="simulator"
          />
        ) : null}
      </div>
    );
  }

  if (!canSimulate) {
    return (
      <div className="cut-simulator-wrap">
        {!showCuts && (
          <p className="export-note cut-simulator-disabled">
            {display.t("web.werkplan.simulator.enableCutSequence")}
          </p>
        )}
        {showCuts && cutSequence.length === 0 && (
          <p className="export-note cut-simulator-disabled">
            {display.t("web.werkplan.simulator.noCuts")}
          </p>
        )}
        <ExportBoard
          {...boardProps}
          sourcePanel={sourcePanel}
          sheet={sheet}
          strategy={strategy}
          showCutSequence={showCuts}
        />
      </div>
    );
  }

  return (
    <div className={`cut-simulator-shell${simulatorOpen ? " is-open" : ""}`}>
      <div className="cut-simulator-chrome no-print">
        <div className="cut-simulator-teaser" hidden={simulatorOpen}>
          <CutSimulatorIcon />
          <div className="cut-simulator-teaser-copy">
            <strong>{display.t("web.werkplan.simulator.teaserTitle")}</strong>
            <p>{display.t("web.werkplan.simulator.teaserCopy")}</p>
          </div>
          <button
            ref={openButtonRef}
            type="button"
            className="secondary-button cut-simulator-teaser-button"
            aria-expanded={simulatorOpen}
            aria-controls={panelId}
            onClick={openSimulator}
          >
            {display.t("web.werkplan.simulator.openButton")}
          </button>
        </div>

        <div id={panelId} className="cut-simulator-panel" hidden={!simulatorOpen}>
          <div className="cut-simulator-header">
            <div className="cut-simulator-title-row">
              <h4
                ref={panelHeadingRef}
                tabIndex={-1}
                className="cut-simulator-panel-title"
              >
                {display.t("web.werkplan.simulator.panelTitle")}
              </h4>
              <button
                type="button"
                className="secondary-button cut-simulator-close"
                onClick={closeSimulator}
              >
                {display.t("web.werkplan.simulator.close")}
              </button>
            </div>
            <p className="export-settings-hint">
              {display.t("web.werkplan.simulator.panelHint")}
              {" "}
              <span className="cut-simulator-keyboard-hint">
                {display.t("web.werkplan.simulator.keyboardHint")}
              </span>
            </p>
          </div>

          <div className="cut-simulator-controls">
            <div className="cut-simulator-nav">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setCurrentStep((value) => Math.max(0, value - 1))}
                disabled={atStart}
              >
                {display.t("web.werkplan.simulator.previous")}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setCurrentStep((value) => Math.min(maxStep, value + 1))}
                disabled={atEnd}
              >
                {display.t("web.werkplan.simulator.next")}
              </button>
            </div>

            <div className="cut-simulator-progress">
              <label className="cut-simulator-slider">
                <span className="visually-hidden">
                  {display.t("web.werkplan.simulator.stepSlider")}
                </span>
                <input
                  type="range"
                  min="0"
                  max={maxStep}
                  value={currentStep}
                  onChange={(event) => setCurrentStep(Number(event.target.value))}
                />
              </label>
              <span className="cut-simulator-step-label">
                {stepProgressLabel}
              </span>
            </div>
          </div>

          <div className="cut-simulator-status" aria-live="polite" aria-atomic="true">
            <div className="cut-simulator-instruction">
              <strong>{stepStatus.title}</strong>
              {stepStatus.detail && <p>{stepStatus.detail}</p>}
            </div>
          </div>
        </div>
      </div>

      {showMobileAppPromo && !isMobileView ? (
        <DismissibleMobileAppPromo
          enableHandoff={enableMobileHandoff}
          layout="tablet"
          onHandoff={onWorkshopHandoff}
          variant="simulator"
        />
      ) : null}

      <ExportBoard
        {...boardProps}
        sourcePanel={sourcePanel}
        sheet={sheet}
        strategy={strategy}
        showCutSequence
        cutSimulationStep={simulatorOpen ? currentStep : null}
        simulationActiveStock={activeStock}
      />
    </div>
  );
}
